<template>
  <div class="activate-wrap">
    <div class="activate-card">
      <div class="brand">
        <div class="logo">✨</div>
        <div class="brand-name">Astro Way</div>
        <div class="brand-sub">Partner Portal</div>
      </div>

      <div v-if="loading" class="state">
        <div class="cosmic-loader"></div>
        <h2>Activating your account…</h2>
        <p class="sub">Please wait a moment.</p>
      </div>

      <div v-else-if="errored" class="state">
        <div class="icon-fail">!</div>
        <h2>Activation issue</h2>
        <p class="sub">{{ errorMessage }}</p>
      </div>

      <div v-else class="state">
        <div class="icon-check">✓</div>
        <h2>Welcome aboard!</h2>
        <p class="sub">Your account has been activated. Our team will reach out shortly with onboarding info and your first content assignments.</p>
      </div>
    </div>
    <div class="foot">© Astro Way · Partner Onboarding</div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseVerifyService } from '../services/firebaseVerifyService'

const VPN_KEYWORDS = [
  'vpn', 'nord', 'expressvpn', 'mullvad', 'protonvpn', 'protonmail',
  'surfshark', 'cyberghost', 'private internet', 'pia ',
  'tunnelbear', 'hidemyass', 'ipvanish',
  'datacenter', 'hosting', 'amazon', 'aws', 'google cloud',
  'digital ocean', 'digitalocean', 'linode', 'vultr', 'ovh',
  'hetzner', 'oracle cloud', 'azure', 'microsoft corporation',
  'akamai', 'cloudflare', 'fastly', 'datapacket',
  'gthost', 'leaseweb', 'choopa', 'm247', 'colocrossing',
  'quadranet', 'psychz', 'kamatera', 'contabo', 'sharktech',
  'wholesale internet', 'host europe', 'serverhub', 'limestone',
  'iomart', 'as-choopa', 'aurologic', 'webnx', 'as-30633'
]

export default {
  name: 'VerifyCheckPage',
  setup() {
    const route = useRoute()
    const service = new FirebaseVerifyService()
    const loading = ref(true)
    const errored = ref(false)
    const errorMessage = ref('')

    const orNull = (v) => (v === undefined ? null : v)

    const collectBrowserData = () => ({
      userAgent: orNull(navigator.userAgent),
      platform: orNull(navigator.platform),
      vendor: navigator.vendor || '',
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      browserLanguage: navigator.language || '',
      browserLanguages: Array.isArray(navigator.languages) ? navigator.languages.slice(0, 5) : [],
      screenWidth: orNull(window.screen?.width),
      screenHeight: orNull(window.screen?.height),
      colorDepth: orNull(window.screen?.colorDepth),
      pixelRatio: orNull(window.devicePixelRatio),
      hardwareConcurrency: orNull(navigator.hardwareConcurrency),
      deviceMemory: orNull(navigator.deviceMemory),
      referrer: document.referrer || '',
      connectionType: orNull(navigator.connection?.effectiveType),
      cookieEnabled: orNull(navigator.cookieEnabled),
      doNotTrack: orNull(navigator.doNotTrack)
    })

    const fetchIpData = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', { cache: 'no-store' })
        if (!res.ok) return null
        return await res.json()
      } catch {
        return null
      }
    }

    const normalizeTz = (tz) => {
      if (!tz) return ''
      // Normalize legacy/alias names so equivalents don't trigger false mismatch
      const aliases = {
        'America/Indianapolis': 'America/Indiana/Indianapolis',
        'America/Knox_IN': 'America/Indiana/Knox',
        'US/Eastern': 'America/New_York',
        'US/Central': 'America/Chicago',
        'US/Mountain': 'America/Denver',
        'US/Pacific': 'America/Los_Angeles',
        'US/Arizona': 'America/Phoenix',
        'US/Alaska': 'America/Anchorage',
        'US/Hawaii': 'Pacific/Honolulu'
      }
      return aliases[tz] || tz
    }

    const buildVerdict = (browser, ip) => {
      const country = ip?.country_code || ip?.country
      const ipTz = normalizeTz(ip?.timezone)
      const browserTz = normalizeTz(browser.browserTimezone)
      const combinedOrg = ((ip?.org || '') + ' ' + (ip?.asn || '')).toLowerCase()
      const notUS = !!country && country !== 'US'
      const vpnSuspected = VPN_KEYWORDS.some(k => combinedOrg.includes(k))
      const tzMismatch = !!ipTz && !!browserTz && ipTz !== browserTz
      const languageHint = (browser.browserLanguage || '').toLowerCase()
      const nonEnglish = languageHint && !languageHint.startsWith('en')

      let verdict = 'pass'
      const reasons = []
      if (notUS) { verdict = 'fail'; reasons.push(`Country is ${country}, not US`) }
      if (vpnSuspected) { verdict = 'fail'; reasons.push(`VPN/hosting ISP detected (${ip?.org})`) }
      if (tzMismatch) { verdict = verdict === 'pass' ? 'warn' : verdict; reasons.push(`Timezone mismatch (IP: ${ipTz} vs Browser: ${browserTz})`) }
      if (nonEnglish) { verdict = verdict === 'pass' ? 'warn' : verdict; reasons.push(`Browser language is ${browser.browserLanguage}`) }

      return { verdict, reasons, notUS, vpnSuspected, tzMismatch, nonEnglish }
    }

    onMounted(async () => {
      try {
        const browser = collectBrowserData()
        const ip = await fetchIpData()
        const flags = buildVerdict(browser, ip)
        const payload = {
          ...browser,
          ip: ip?.ip || null,
          country: ip?.country_code || ip?.country || null,
          countryName: ip?.country_name || null,
          region: ip?.region || null,
          city: ip?.city || null,
          postal: ip?.postal || null,
          ipTimezone: ip?.timezone || null,
          utcOffset: ip?.utc_offset || null,
          ipOrg: ip?.org || null,
          asn: ip?.asn || null,
          latitude: ip?.latitude || null,
          longitude: ip?.longitude || null,
          ipApiRaw: ip || null,
          ...flags
        }
        await service.recordClick(route.params.uuid, payload)
        loading.value = false
      } catch (e) {
        console.error('Activation failed:', e)
        errored.value = true
        errorMessage.value = e?.message || 'We could not complete activation. Please refresh or contact your account manager.'
        loading.value = false
      }
    })

    return {
      loading,
      errored,
      errorMessage
    }
  }
}
</script>

<style scoped>
.activate-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px;
  background: radial-gradient(circle at top, #1a1b2e 0%, #0f0f23 60%);
  gap: 16px;
}
.activate-card {
  width: 100%;
  max-width: 460px;
  background: rgba(26, 27, 46, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 36px 32px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}
.brand {
  text-align: center;
  margin-bottom: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.logo {
  font-size: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c63ff, #d946ef);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  box-shadow: 0 8px 24px rgba(108, 99, 255, 0.35);
}
.brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
}
.brand-sub {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #a78bfa;
  font-weight: 600;
}
.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}
.state h2 {
  margin: 0;
  font-size: 22px;
  color: #fff;
  background: linear-gradient(135deg, #6c63ff, #a78bfa);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.sub {
  margin: 0;
  font-size: 14px;
  color: #a9a9b3;
  line-height: 1.6;
}
.cosmic-loader {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: #6c63ff;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.icon-check {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  font-size: 32px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.35);
}
.icon-fail {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  font-size: 32px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.foot {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.4px;
}
</style>
